/**
 * Copyright 2018 Phenix Inc. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
define([
    'phenix-web-lodash-light',
    'phenix-web-assert',
    'phenix-web-event',
    'phenix-web-global',
    'phenix-web-network-connection-monitor',
    'phenix-web-http',
    'phenix-web-observable',
    'phenix-web-disposable'
], function(_, assert, event, global, networkConnectionMonitor, http, observable, disposable) {
    var networkDisconnectHysteresisInterval = 0;
    var events = {
        capacity: 'capacity',
        error: 'error',
        response: 'response'
    };
    var localStoreKeyPrefix = '__phenixBatchHttpMessages';
    // Use a global cache so we only need to check once
    var localStorageSupported = null;

    function BatchHttp(uri, options, serializeRequest, deserializeResponse) {
        assert.isStringNotEmpty(uri, 'uri');

        options = options || {};

        assert.isObject(options, 'options');

        if (options.requestType) {
            assert.isStringNotEmpty(options.requestType, 'options.requestType');
        }

        if (options.maxAttempts) {
            assert.isNumber(options.maxAttempts, 'options.maxAttempts');
        }

        if (options.maxBufferedRecords) {
            assert.isNumber(options.maxBufferedRecords, 'options.maxBufferedRecords');
        }

        if (options.maxBatchSize) {
            assert.isNumber(options.maxBatchSize, 'options.maxBatchSize');
        }

        if (options.contentType) {
            assert.isStringNotEmpty(options.contentType, 'options.maxBatchSize');
        }

        if (serializeRequest) {
            assert.isFunction(serializeRequest, 'serializeRequest');
        }

        if (deserializeResponse) {
            assert.isFunction(deserializeResponse, 'deserializeResponse');
        }

        this._localStoreKey = localStoreKeyPrefix + '|' + uri;
        this._requestType = options.requestType || 'POST';
        this._requestTimeout = options.requestTimeout || 30000;
        this._maxAttempts = options.maxAttempts || 5;
        this._maxBufferedRecords = options.maxBufferedRecords || 1000;
        this._maxBatchSize = options.maxBatchSize || 100;
        this._contentType = options.contentType || 'application/json';
        this._serializeRequest = serializeRequest;
        this._deserializeResponse = deserializeResponse;
        this._uri = uri;
        this._records = getStoredMessages.call(this) || [];
        this._isEnabled = true;
        this._namedEvents = new event.NamedEvents();
        this._canSendRequestObservable = new observable.Observable(true);
        this._disposables = new disposable.DisposableList();
        this._pending = false;
        this._unloadTriggered = false;
        this._localStorageEnabled = localStorageSupported === null ? localStorageSupported = isLocalStorageSupported.call(this) : localStorageSupported;

        this._disposables.add(this._canSendRequestObservable.subscribe(_.bind(sendMessagesIfAble, this)));
        this._disposables.add(startNetworkConnectionMonitor.call(this));
        this._disposables.add(this._namedEvents);

        setStoredMessages.call(this, []);

        sendMessagesIfAble.call(this);

        var that = this;

        _.addEventListener(global, 'beforeunload', function() {
            appendStoredMessages.call(that, that._records);

            that._unloadTriggered = true;

            that._records = [];
        });
    }

    BatchHttp.prototype.on = function on(eventName, callback) {
        assert.isStringNotEmpty(eventName, 'eventName');
        assert.isFunction(callback, 'callback');

        return this._namedEvents.listen(eventName, callback);
    };

    BatchHttp.prototype.addRecord = function(record) {
        if (this._unloadTriggered) {
            appendStoredMessages.call(this, [record]);

            return;
        }

        this._records.push(record);

        sendMessagesIfAble.call(this);
        deleteRecordsIfAtCapacity.call(this);
    };

    BatchHttp.prototype.addRecordToBeginning = function(record) {
        this._records.unshift(record);

        sendMessagesIfAble.call(this);
        deleteRecordsIfAtCapacity.call(this);
    };

    BatchHttp.prototype.dispose = function dispose() {
        this._records = [];

        this._disposables.dispose();
    };

    function deleteRecordsIfAtCapacity() {
        if (this._records.length > this._maxBufferedRecords) {
            var deleteRecords = this._records.length - (this._maxBufferedRecords / 2);

            this._records = this._records.slice(deleteRecords);

            this._namedEvents.fireAsync(events.capacity, [null, {numberOfDeletedRecords: deleteRecords}]);
        }
    }

    function sendMessagesIfAble() {
        if (!this._canSendRequestObservable.getValue() || this._records.length === 0) {
            return;
        }

        setStatus.call(this, true);

        var storeRecords = {records: _.take(this._records, this._maxBatchSize)};

        this._records = this._records.slice(this._maxBatchSize);

        var that = this;

        try {
            sendHttpRequest.call(this, this._uri, storeRecords, function onTimeout() {
                setTimeout(function waitForDisconnectTimeout() {
                    that._records = that._records.concat(storeRecords.records);

                    setStatus.call(that, false);
                }, networkDisconnectHysteresisInterval);
            });
        } catch (e) {
            setTimeout(function() {
                setStatus.call(that, false);

                return that._namedEvents.fire(events.error, [e]);
            }, networkDisconnectHysteresisInterval);
        }
    }

    function startNetworkConnectionMonitor() {
        var that = this;

        function onNetworkStatusChange() {
            setStatus.call(that, that._pending);
        }

        return networkConnectionMonitor.listenForNetworkChange(onNetworkStatusChange);
    }

    function sendHttpRequest(url, dataToSend, onTimeout) {
        var that = this;

        var data = this._serializeRequest ? this._serializeRequest(_.assign({}, dataToSend)) : dataToSend;

        function handleResponse(error, response) {
            if (error) {
                if (error.message === 'timeout' && !isAtMaxRecords.call(that)) {
                    return onTimeout();
                }

                setStatus.call(that, false);

                return that._namedEvents.fire(events.error, [error]);
            }

            setStatus.call(that, false);

            var parsedResponse = that._deserializeResponse ? that._deserializeResponse(response.data) : response.data;

            return that._namedEvents.fire(events.response, [parsedResponse]);
        }

        switch (this._requestType) {
        case 'POST':
            return http.postWithRetry(url, data, {
                timeout: this._requestTimeout,
                contentType: this._contentType,
                retryOptions: {
                    maxAttempts: this._maxAttempts,
                    additionalRetryErrorCodes: [0]
                }
            }, handleResponse);
        default:
            throw new Error('Invalid request type');
        }
    }

    function isAtMaxRecords() {
        return this._records.length >= this._maxBufferedRecords;
    }

    function setStatus(pending) {
        this._pending = pending;

        this._canSendRequestObservable.setValue(!pending && networkConnectionMonitor.isOnline());
    }

    var getStoredMessages = function getStoredMessages() {
        if (!this._localStorageEnabled) {
            return [];
        }

        try {
            var storedMessages = global.localStorage.getItem(this._localStoreKey);

            if (storedMessages) {
                return JSON.parse(storedMessages);
            }
        } catch (e) {
            console.log('Failed to read local storage [' + e.message + ']');
        }

        return [];
    };

    var appendStoredMessages = function appendStoredMessages(messages) {
        if (!this._localStorageEnabled || !messages || messages.length === 0) {
            return;
        }

        var messagesToStore = getStoredMessages.call(this).concat(messages);

        setStoredMessages.call(this, messagesToStore);
    };

    var setStoredMessages = function setStoredMessages(messages) {
        if (!this._localStorageEnabled || !messages) {
            return;
        }

        try {
            var json = JSON.stringify(messages);

            global.localStorage.setItem(this._localStoreKey, json);
        } catch (e) {
            console.log('Failed to store data in local storage [' + e.message + ']');
        }
    };

    var isLocalStorageSupported = function isLocalStorageSupported() {
        try {
            if (!global.localStorage){
                console.log('Local storage not available');

                return false;
            }

            var key = '__is_local_storage_supported__';

            global.localStorage.setItem(key, key);

            var value = global.localStorage.getItem(key);

            global.localStorage.removeItem(key);

            if (value !== key) {
                console.log('Local storage is passively blocked');
            }

            return value === key;
        } catch (e) {
            console.log('Local storage is blocked');

            return false;
        }
    };

    return BatchHttp;
});